import { useState, useEffect } from "react";
import { Camera, Plane, Cpu, Eye, EyeOff, Sparkles, Loader2 } from "lucide-react";
import { adminKitOptions, adminDetectKitItem, adminBehindTheLens } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_KIT = "DJI Mavic 4 Pro";

const isDroneKit = (kit, drones) => !!kit && drones.includes(kit);

// Field display order for the public panel - also drives the admin layout
const DRONE_FIELDS = ["kit_used", "flight_style", "focal_length", "aperture", "shutter_speed", "iso", "long_exposure"];
const GROUND_FIELDS = ["kit_used", "lens", "focal_length", "aperture", "shutter_speed", "iso", "long_exposure"];

const LABELS = {
  kit_used: "Kit",
  flight_style: "Flight Style",
  lens: "Lens",
  focal_length: "Focal Length",
  aperture: "Aperture",
  shutter_speed: "Shutter Speed",
  iso: "ISO",
  long_exposure: "Long Exposure",
  lighting_conditions: "Lighting",
  shoot_notes: "Shoot notes",
};

const HideEye = ({ hidden, onToggle, testid }) => (
  <button
    type="button"
    onClick={onToggle}
    title={hidden ? "Hidden from public, click to show" : "Visible publicly, click to hide"}
    data-testid={testid}
    className="p-1 hover:bg-white/5 rounded"
  >
    {hidden ? <EyeOff size={13} className="text-[#525252]" /> : <Eye size={13} className="text-[#4A6D8C]" />}
  </button>
);

const TextField = ({ field, value, placeholder, aiSet, onUpdate, testidPrefix }) => (
  <input
    type="text"
    value={value || ""}
    onChange={(e) => {
      const v = e.target.value || null;
      const ai = new Set(aiSet);
      ai.delete(field);
      onUpdate({ [field]: v, ai_suggested: Array.from(ai) });
    }}
    placeholder={placeholder || ""}
    className="hl-input py-1.5 text-[12px]"
    data-testid={`${testidPrefix}-${field}`}
  />
);

const AiBadge = () => (
  <span
    title="AI suggested, edit or accept"
    className="inline-flex items-center gap-0.5 text-[8px] tracking-[0.18em] uppercase text-[#658CAE] border border-[#4A6D8C]/40 px-1 py-[1px] ml-1.5"
  >
    <Sparkles size={8} /> AI
  </span>
);

const Row = ({ label, isHidden, onToggleHide, isAi, hideTestid, children }) => (
  <div className={`grid grid-cols-[140px_1fr_28px] gap-3 items-center border-b border-white/[0.04] py-2 ${isHidden ? "opacity-50" : ""}`}>
    <div className="text-[10px] tracking-[0.22em] uppercase text-[#737373] flex items-center">
      {label}
      {isAi && <AiBadge />}
    </div>
    <div>{children}</div>
    <HideEye hidden={isHidden} onToggle={onToggleHide} testid={hideTestid} />
  </div>
);

export default function ShotDetailsEditor({ technical, onChange, testidPrefix = "tech", itemId = null }) {
  const t = technical || {};
  const [kit, setKit] = useState({ cameras: [], drones: [] });
  const [detectingKit, setDetectingKit] = useState(false);
  const [fillingTech, setFillingTech] = useState(false);
  useEffect(() => {
    adminKitOptions().then((k) => setKit({ cameras: k.cameras || [], drones: k.drones || [] })).catch(() => {});
  }, []);

  const update = (patch) => onChange({ ...t, ...patch });

  const detectKit = async () => {
    if (!itemId) return;
    setDetectingKit(true);
    try {
      const res = await adminDetectKitItem(itemId);
      const wasDrone = kit.drones.includes(t.kit_used);
      const isDrone = res.is_drone;
      // Clear flight_style if switching from drone to ground
      const patch = { kit_used: res.kit_used };
      if (!isDrone && wasDrone) patch.flight_style = null;
      update(patch);
      toast.success(`AI says: ${res.kit_used} (${isDrone ? "drone" : "ground"} shot)`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kit detection failed.");
    } finally {
      setDetectingKit(false);
    }
  };

  const fillTech = async () => {
    if (!itemId) return;
    setFillingTech(true);
    try {
      const data = await adminBehindTheLens(itemId, { kit_used: t.kit_used || null });
      const BLANK = ["", "Unknown", "Not available", "Not Available", "unknown"];
      const isBlank = (v) => v == null || (typeof v === "string" && (v.trim() === "" || BLANK.includes(v.trim())));
      const techSugg = data.tech_suggestions || {};
      const currentAi = new Set(t.ai_suggested || []);
      const patch = {};
      for (const [k, v] of Object.entries(techSugg)) {
        if (isBlank(t[k])) {
          patch[k] = v;
          currentAi.add(k);
        }
      }
      patch.ai_suggested = Array.from(currentAi);
      update(patch);
      const filled = Object.keys(patch).filter((k) => k !== "ai_suggested").length;
      toast.success(filled ? `${filled} tech field${filled > 1 ? "s" : ""} suggested.` : "No new suggestions, fields already populated.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Tech fill failed.");
    } finally {
      setFillingTech(false);
    }
  };

  const droneKit = isDroneKit(t.kit_used, kit.drones);
  const fieldOrder = droneKit ? DRONE_FIELDS : GROUND_FIELDS;
  const aiSuggested = new Set(t.ai_suggested || []);
  const hidden = new Set(t.hidden_fields || []);

  const setHidden = (field) => {
    const next = new Set(hidden);
    if (next.has(field)) next.delete(field);
    else next.add(field);
    update({ hidden_fields: Array.from(next) });
  };

  // Helpers: text input / dropdown for a field; admin overrides AI suggestion by typing
  const textFieldFor = (field, placeholder) => (
    <TextField
      field={field}
      value={t[field]}
      placeholder={placeholder}
      aiSet={aiSuggested}
      onUpdate={update}
      testidPrefix={testidPrefix}
    />
  );

  const headerLabel = droneKit ? "Flight Details" : "Shot Details";
  const headerIcon = droneKit ? <Plane size={12} /> : <Camera size={12} />;

  return (
    <div className="bg-[#0f0f0f] border border-[#1a1a1a] p-5" data-testid={`${testidPrefix}-panel`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C]">
          {headerIcon} {headerLabel}
        </div>
        {itemId && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={detectKit}
              disabled={detectingKit}
              data-testid={`${testidPrefix}-detect-kit`}
              className="text-[10px] tracking-[0.22em] uppercase text-[#4A6D8C] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              title="Ask AI to detect the kit from the image"
            >
              {detectingKit ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
              {detectingKit ? "Detecting…" : "AI detect kit"}
            </button>
            <span className="text-[#404040]">·</span>
            <button
              type="button"
              onClick={fillTech}
              disabled={fillingTech}
              data-testid={`${testidPrefix}-fill-tech`}
              className="text-[10px] tracking-[0.22em] uppercase text-[#4A6D8C] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              title="Ask AI to suggest values for empty tech fields"
            >
              {fillingTech ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
              {fillingTech ? "Filling…" : "AI fill tech"}
            </button>
          </div>
        )}
      </div>

      {/* Each field */}
      {fieldOrder.map((field) => {
        const isAi = aiSuggested.has(field);
        const isHidden = hidden.has(field);
        const label = LABELS[field] || field;
        const toggleHide = () => setHidden(field);

        if (field === "kit_used") {
          return (
            <Row key={field} label={label} isHidden={false} onToggleHide={() => {}} isAi={isAi}
                 hideTestid={`${testidPrefix}-hide-${field}`}>
              <select
                value={t.kit_used || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  // Clear flight_style if switching away from drone
                  const patch = { kit_used: v || null };
                  if (!kit.drones.includes(v)) patch.flight_style = null;
                  update(patch);
                }}
                className="hl-input py-1.5 text-[12px]"
                data-testid={`${testidPrefix}-kit-select`}
              >
                <option value="">Select kit</option>
                <optgroup label="Drones">
                  {kit.drones.map((d) => <option key={d} value={d}>{d}</option>)}
                </optgroup>
                <optgroup label="Cameras">
                  {kit.cameras.map((c) => <option key={c} value={c}>{c}</option>)}
                </optgroup>
                <option value="Other">Other / Unknown</option>
              </select>
            </Row>
          );
        }

        if (field === "flight_style") {
          return (
            <Row key={field} label={label} isHidden={isHidden} onToggleHide={toggleHide} isAi={isAi}
                 hideTestid={`${testidPrefix}-hide-${field}`}>
              <input list={`${testidPrefix}-fs-list`} type="text"
                value={t.flight_style || ""}
                onChange={(e) => {
                  const v = e.target.value || null;
                  const ai = new Set(aiSuggested); ai.delete(field);
                  update({ flight_style: v, ai_suggested: Array.from(ai) });
                }}
                className="hl-input py-1.5 text-[12px]"
                data-testid={`${testidPrefix}-flight-style`}
                placeholder="e.g. Top Down Survey"
              />
              <datalist id={`${testidPrefix}-fs-list`}>
                {(kit.flight_styles || [
                  "Aerial Overview","Top Down Survey","Oblique Perspective",
                  "Infrastructure Inspection","Progress Monitoring","Site Context Capture",
                  "Depot Overview","Road Corridor Capture","Structure Inspection",
                ]).map((s) => <option key={s} value={s} />)}
              </datalist>
            </Row>
          );
        }

        if (field === "long_exposure") {
          return (
            <Row key={field} label={label} isHidden={isHidden} onToggleHide={toggleHide} isAi={isAi}
                 hideTestid={`${testidPrefix}-hide-${field}`}>
              <div className="flex gap-2">
                {["Yes", "No"].map((opt) => (
                  <button key={opt} type="button"
                    onClick={() => {
                      const ai = new Set(aiSuggested); ai.delete(field);
                      update({ long_exposure: opt, ai_suggested: Array.from(ai) });
                    }}
                    className={`hl-chip ${t.long_exposure === opt ? "active" : ""}`}
                    data-testid={`${testidPrefix}-long-${opt.toLowerCase()}`}>
                    {opt}
                  </button>
                ))}
              </div>
            </Row>
          );
        }

        return (
          <Row key={field} label={label} isHidden={isHidden} onToggleHide={toggleHide} isAi={isAi}
               hideTestid={`${testidPrefix}-hide-${field}`}>
            {textFieldFor(field, field === "focal_length" ? "e.g. 24mm" : field === "lens" ? "e.g. 24-70mm" : "")}
          </Row>
        );
      })}

      {/* Admin-only contextual fields (always rendered, not in DRONE/GROUND list) */}
      <div className="mt-4 pt-3 border-t border-white/[0.04]">
        <div className="text-[9px] tracking-[0.28em] uppercase text-[#525252] mb-2">Notes (admin)</div>

        <Row label="Lighting" isHidden={hidden.has("lighting_conditions")}
             onToggleHide={() => setHidden("lighting_conditions")} isAi={aiSuggested.has("lighting_conditions")}
             hideTestid={`${testidPrefix}-hide-lighting_conditions`}>
          <input type="text"
            value={t.lighting_conditions || ""}
            onChange={(e) => update({ lighting_conditions: e.target.value || null })}
            placeholder="e.g. Night works, overcast, golden hour"
            className="hl-input py-1.5 text-[12px]"
            data-testid={`${testidPrefix}-lighting`}
          />
        </Row>

        <Row label="Shoot notes" isHidden={hidden.has("shoot_notes")}
             onToggleHide={() => setHidden("shoot_notes")} isAi={false}
             hideTestid={`${testidPrefix}-hide-shoot_notes`}>
          <textarea rows={2}
            value={t.shoot_notes || ""}
            onChange={(e) => update({ shoot_notes: e.target.value || null })}
            placeholder="Optional one-liner shown under Behind the Lens in the lightbox."
            className="hl-input py-1.5 text-[12px]"
            data-testid={`${testidPrefix}-notes`}
          />
        </Row>

        {t.gps_lat != null && t.gps_lng != null && (
          <div className="grid grid-cols-[140px_1fr_28px] gap-3 items-center py-2">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#737373]">GPS (internal)</div>
            <div className="text-[12px] text-[#c9d1d9]">
              {Number(t.gps_lat).toFixed(4)}, {Number(t.gps_lng).toFixed(4)}
              <label className="ml-3 inline-flex items-center gap-2 text-[11px] text-[#A3A3A3]">
                <input type="checkbox"
                  checked={!!t.show_gps_publicly}
                  onChange={(e) => update({ show_gps_publicly: e.target.checked })}
                  className="w-3.5 h-3.5 accent-[#4A6D8C]"
                  data-testid={`${testidPrefix}-show-gps`} />
                Show publicly
              </label>
            </div>
            <span />
          </div>
        )}
      </div>

      <div className="text-[10px] text-[#525252] mt-3 leading-relaxed flex items-center gap-1.5">
        <Cpu size={10} /> EXIF auto-fills from the original camera file. AI suggestions are marked with
        <span className="inline-flex items-center gap-0.5 text-[8px] tracking-[0.18em] uppercase text-[#658CAE]">
          <Sparkles size={8} /> AI
        </span>
        - edit to override. The eye toggle hides a row from the public lightbox.
      </div>
    </div>
  );
}

export { DEFAULT_KIT };
